import { Plus, Warehouse, X } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Property, NewApartmentForm } from './types';

interface AddApartmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: NewApartmentForm;
  onFormChange: (form: NewApartmentForm) => void;
  onSave: () => void;
  properties: Property[];
  t: (key: string) => string;
}

export function AddApartmentDialog({
  open,
  onOpenChange,
  form,
  onFormChange,
  onSave,
  properties,
  t
}: AddApartmentDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="btn-press" data-testid="add-apartment-button">
          <Plus size={18} className="mr-2" />
          {t('add')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Lägg till lägenhet</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>{t('properties')} *</Label>
            <Select
              value={form.property_id}
              onValueChange={(value) => onFormChange({ ...form, property_id: value })}
            >
              <SelectTrigger data-testid="apt-property-select">
                <SelectValue placeholder="Välj fastighet" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t('apartment_number')} *</Label>
            <Input
              value={form.apartment_number}
              onChange={(e) => onFormChange({ ...form, apartment_number: e.target.value })}
              placeholder="1001"
              className="font-mono"
              data-testid="apt-number-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('floor')}</Label>
              <Input
                type="number"
                value={form.floor}
                onChange={(e) => onFormChange({ ...form, floor: e.target.value })}
                placeholder="1"
                data-testid="apt-floor-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('area')}</Label>
              <Input
                type="number"
                value={form.area_sqm}
                onChange={(e) => onFormChange({ ...form, area_sqm: e.target.value })}
                placeholder="75"
                data-testid="apt-area-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t('dwelling_type')}</Label>
            <Select
              value={form.dwelling_type}
              onValueChange={(value) => onFormChange({ ...form, dwelling_type: value })}
            >
              <SelectTrigger data-testid="apt-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="apartment">{t('apartment')}</SelectItem>
                <SelectItem value="house">{t('house')}</SelectItem>
                <SelectItem value="townhouse">{t('townhouse')}</SelectItem>
                <SelectItem value="studio">{t('studio')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Storage Units (Förråd) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Warehouse size={16} />
              Förråd
            </Label>
            <div className="space-y-2">
              {(form.storage_units || []).map((storage, index) => (
                <div key={`storage-${index}-${storage}`} className="flex gap-2 items-center">
                  <Input
                    value={storage}
                    onChange={(e) => {
                      const updated = [...(form.storage_units || [])];
                      updated[index] = e.target.value;
                      onFormChange({ ...form, storage_units: updated });
                    }}
                    placeholder="t.ex. Förråd 101, Cykelrum B"
                    className="flex-1"
                  />
                  <button
                    onClick={() => {
                      const updated = (form.storage_units || []).filter((_, i) => i !== index);
                      onFormChange({ ...form, storage_units: updated });
                    }}
                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                    title="Ta bort"
                    type="button"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const updated = [...(form.storage_units || []), ''];
                  onFormChange({ ...form, storage_units: updated });
                }}
                className="w-full"
              >
                <Plus size={14} className="mr-1" />
                Lägg till förråd
              </Button>
            </div>
          </div>

          <Button onClick={onSave} className="w-full" data-testid="save-apartment-button">
            {t('save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
